import { useEffect, useState } from "react";
import { Building2, HardHat, Landmark, Truck, Mountain, ShieldCheck, FileText, Users, CalendarRange, Check, Star, Quote, PhoneCall } from "lucide-react";
import { endpoints, api } from "../lib/api";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { toast } from "sonner";

const INDUSTRIES = [
  [HardHat, "Construction"], [Landmark, "Government"], [ShieldCheck, "Insurance"],
  [Building2, "Engineering"], [Truck, "Transport"], [Mountain, "Mining"],
];
const FEATURES = [
  [CalendarRange, "Long-term accommodation", "Weekly and monthly rates for extended projects and rotating crews."],
  [Users, "Group bookings", "Reserve multiple rooms and interconnecting layouts for your whole team."],
  [FileText, "Invoice facilities", "Streamlined direct billing and invoicing for approved corporate accounts."],
];

export default function Corporate() {
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", phone: "", industry: "", guests: "", duration: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Quick "one-tap" call-back enquiry — 3 fields, submits to same corporate endpoint.
  const [quick, setQuick] = useState({ contact_name: "", email: "", phone: "" });
  const [quickSent, setQuickSent] = useState(false);
  const [quickBusy, setQuickBusy] = useState(false);
  const qset = (k) => (e) => setQuick({ ...quick, [k]: e.target.value });
  const submitQuick = async (e) => {
    e.preventDefault();
    if (!quick.contact_name || !(quick.email || quick.phone)) { toast.error("Please share your name and either email or phone."); return; }
    setQuickBusy(true);
    try {
      await endpoints.corporate({ company: "(Quick enquiry)", contact_name: quick.contact_name, email: quick.email || "", phone: quick.phone || "", industry: "", guests: "", duration: "", message: "Please call me back with corporate rates." });
      setQuickSent(true);
      toast.success("Thanks — our team will call you back within one business day.");
    } catch { toast.error("Something went wrong. Please call 02 6036 2077."); }
    finally { setQuickBusy(false); }
  };

  // Testimonials (manually curated real reviews via admin CRUD; no fake data).
  const [reviews, setReviews] = useState([]);
  useEffect(() => { api.get("/reviews").then((r) => setReviews(r.data || [])).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.contact_name || !form.email) { toast.error("Please complete the required fields."); return; }
    setBusy(true);
    try {
      const r = await endpoints.corporate(form);
      setSent(true);
      toast.success(r.message || "Enquiry received.");
    } catch { toast.error("Something went wrong. Please call 02 6036 2077."); }
    finally { setBusy(false); }
  };

  const inp = "w-full bg-white border border-[#0B192C]/15 rounded-xl px-4 py-3 text-[#222] outline-none focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 transition";

  return (
    <div>
      <PageHero overline="For Business" title="Corporate Accommodation"
        subtitle="Reliable, comfortable stays for teams, contractors and government travellers — with rates and invoicing built for business."
        image="/static/generated/byer-room-single.png" />

      {/* QUICK CALL-BACK STRIP */}
      <section className="bg-[#0B192C] border-y border-[#D4AF37]/20 py-6" data-testid="quick-enquiry">
        <div className="max-w-6xl mx-auto px-5 md:px-8 flex flex-col lg:flex-row items-start lg:items-center gap-4 lg:gap-6">
          <div className="flex items-center gap-3 shrink-0">
            <span className="w-10 h-10 rounded-full bg-[#D4AF37]/15 grid place-items-center text-[#D4AF37]"><PhoneCall size={18} /></span>
            <div>
              <p className="overline text-[#D4AF37]">Get a call back</p>
              <p className="text-white font-serif text-xl leading-tight">Corporate rates in under a minute</p>
            </div>
          </div>
          {quickSent ? (
            <p className="lg:ml-auto text-[#D4AF37] font-serif text-lg" data-testid="quick-success">
              <Check className="inline mr-2" size={16} /> Thanks — we'll be in touch within one business day.
            </p>
          ) : (
            <form onSubmit={submitQuick} className="flex-1 w-full lg:ml-auto grid grid-cols-1 sm:grid-cols-4 gap-2" data-testid="quick-form">
              <input className="bg-white/95 rounded-full px-4 py-2.5 text-sm text-[#0B192C] placeholder:text-[#0B192C]/50 outline-none focus:ring-2 focus:ring-[#D4AF37]" placeholder="Your name *" value={quick.contact_name} onChange={qset("contact_name")} data-testid="quick-name" />
              <input className="bg-white/95 rounded-full px-4 py-2.5 text-sm text-[#0B192C] placeholder:text-[#0B192C]/50 outline-none focus:ring-2 focus:ring-[#D4AF37]" placeholder="Work email" type="email" value={quick.email} onChange={qset("email")} data-testid="quick-email" />
              <input className="bg-white/95 rounded-full px-4 py-2.5 text-sm text-[#0B192C] placeholder:text-[#0B192C]/50 outline-none focus:ring-2 focus:ring-[#D4AF37]" placeholder="Phone" value={quick.phone} onChange={qset("phone")} data-testid="quick-phone" />
              <button disabled={quickBusy} className="bg-[#D4AF37] hover:bg-white text-[#0B192C] rounded-full px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60" data-testid="quick-submit">
                {quickBusy ? "Sending…" : "Request Call Back"}
              </button>
            </form>
          )}
        </div>
      </section>

      <section className="bg-[#F7F5F0] py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal><p className="overline text-[#D4AF37] mb-8 text-center">Trusted by teams across industries</p></Reveal>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-24">
            {INDUSTRIES.map(([Icon, l], i) => (
              <Reveal key={l} delay={i * 0.05}>
                <div className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-white soft-shadow border border-[#0B192C]/5">
                  <Icon size={30} className="text-[#0B192C]" /><span className="text-sm text-[#222]/70">{l}</span>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-7 mb-24">
            {FEATURES.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 0.1}>
                <div className="p-8 rounded-2xl border border-[#D4AF37]/30 bg-white soft-shadow h-full">
                  <Icon size={28} className="text-[#D4AF37] mb-4" />
                  <h3 className="font-serif text-2xl text-[#0B192C] mb-2">{t}</h3>
                  <p className="text-[#222]/70 font-light leading-relaxed">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* TESTIMONIAL STRIP — real reviews from admin CRUD, no fabricated data */}
          {reviews.length > 0 && (
            <div className="mb-24" data-testid="corp-testimonials">
              <Reveal><p className="overline text-[#D4AF37] text-center mb-3">What business travellers say</p></Reveal>
              <div className="grid md:grid-cols-3 gap-5">
                {reviews.slice(0, 3).map((r, i) => (
                  <Reveal key={r.id} delay={i * 0.08}>
                    <blockquote className="relative bg-white rounded-2xl p-6 soft-shadow border border-[#0B192C]/5 h-full" data-testid={`testimonial-${i}`}>
                      <Quote size={20} className="text-[#D4AF37]/40 mb-3" />
                      <p className="text-[#222]/80 font-light leading-relaxed text-[15px] mb-4 line-clamp-5">{r.text}</p>
                      <div className="flex items-center gap-1 mb-2">
                        {Array.from({ length: Math.max(0, Math.min(5, Math.round(r.rating || 5))) }).map((_, k) => (
                          <Star key={k} size={13} className="text-[#D4AF37]" fill="#D4AF37" />
                        ))}
                      </div>
                      <footer className="text-[#0B192C] text-sm font-medium">{r.author}</footer>
                    </blockquote>
                  </Reveal>
                ))}
              </div>
            </div>
          )}

          {/* FORM */}
          <div className="grid md:grid-cols-5 gap-12 items-start" id="enquiry">
            <Reveal className="md:col-span-2">
              <h2 className="font-serif text-5xl text-[#0B192C] leading-tight">Request Corporate Rates</h2>
              <p className="text-[#222]/70 mt-4 font-light leading-relaxed">Tell us about your team and dates. Our team responds within one business day with a tailored proposal.</p>
              <div className="mt-6 space-y-3">
                {["Dedicated account manager", "Direct booking rates", "Flexible cancellation for business"].map((x) => (
                  <p key={x} className="flex items-center gap-2 text-[#0B192C]"><Check size={16} className="text-[#D4AF37]" /> {x}</p>
                ))}
              </div>
            </Reveal>

            <Reveal className="md:col-span-3" delay={0.1}>
              {sent ? (
                <div className="bg-white rounded-2xl p-10 soft-shadow text-center border border-[#D4AF37]/30" data-testid="corp-success">
                  <div className="w-14 h-14 rounded-full bg-[#D4AF37] grid place-items-center mx-auto mb-4"><Check className="text-[#0B192C]" /></div>
                  <h3 className="font-serif text-3xl text-[#0B192C]">Thank you</h3>
                  <p className="text-[#222]/70 mt-2">Our team will be in touch within one business day.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-white rounded-2xl p-7 soft-shadow border border-[#0B192C]/5 grid grid-cols-2 gap-4" data-testid="corporate-form">
                  <input className={inp} placeholder="Company *" value={form.company} onChange={set("company")} data-testid="corp-company" />
                  <input className={inp} placeholder="Contact name *" value={form.contact_name} onChange={set("contact_name")} data-testid="corp-name" />
                  <input className={inp} placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid="corp-email" />
                  <input className={inp} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="corp-phone" />
                  <input className={inp} placeholder="Industry" value={form.industry} onChange={set("industry")} data-testid="corp-industry" />
                  <input className={inp} placeholder="No. of guests / rooms" value={form.guests} onChange={set("guests")} data-testid="corp-guests" />
                  <input className={`${inp} col-span-2`} placeholder="Approx. dates / duration" value={form.duration} onChange={set("duration")} data-testid="corp-duration" />
                  <textarea className={`${inp} col-span-2 resize-none`} rows={4} placeholder="Anything else we should know?" value={form.message} onChange={set("message")} data-testid="corp-message" />
                  <button disabled={busy} className="col-span-2 bg-[#0B192C] text-white py-4 rounded-xl font-medium hover:bg-[#D4AF37] hover:text-[#0B192C] transition-colors disabled:opacity-60" data-testid="corp-submit">
                    {busy ? "Sending…" : "Request Corporate Rates"}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
